import React, { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

/**
 * AI-powered assistant panel for risk management.
 * Can suggest risks, scores, and survey questions.
 *
 * Props:
 *   mode: 'risks' | 'score' | 'survey'
 *   context: { department, domain, riskStatement, controls, methodology, topic, audience }
 *   onAcceptRisk: (risk) => void — called when user accepts a suggested risk
 *   onAcceptScore: (scores) => void — called when user accepts suggested scores
 *   onAcceptQuestions: (questions) => void — called when user accepts survey questions
 */
export default function AIAssistPanel({ mode = 'risks', context = {}, onAcceptRisk, onAcceptScore, onAcceptQuestions }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setResults(null);

    try {
      let endpoint, body;

      if (mode === 'risks') {
        endpoint = `${API_BASE}/risk-ai/suggest-risks`;
        body = {
          department: context.department,
          domain: context.domain,
          recent_findings: context.recentFindings,
          additional_context: context.additionalContext,
          parent_risk: context.parentRisk,
          siblings: context.siblings
        };
      } else if (mode === 'score') {
        endpoint = `${API_BASE}/risk-ai/suggest-score`;
        body = {
          risk_statement: context.riskStatement,
          current_controls: context.controls,
          methodology: context.methodology || 'likelihood_impact_5x5'
        };
      } else if (mode === 'survey') {
        endpoint = `${API_BASE}/risk-ai/generate-survey`;
        body = {
          topic: context.topic,
          target_audience: context.audience,
          question_count: context.questionCount || 10
        };
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (data.success) {
        setResults(data);
      } else {
        setError(data.message || 'AI service unavailable');
      }
    } catch (err) {
      setError('Failed to connect to AI service');
    }
    setLoading(false);
  };

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl text-amber-700 text-sm font-medium hover:from-amber-100 hover:to-orange-100 transition-all"
      >
        Assistant — {mode === 'risks' ? 'Suggest Risks' : mode === 'score' ? 'Suggest Scores' : 'Generate Questions'}
      </button>
    );
  }

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-amber-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-amber-800">Assistant</span>
          <span className="text-xs text-amber-500">Azure OpenAI</span>
        </div>
        <button onClick={() => { setExpanded(false); setResults(null); }} className="text-amber-400 hover:text-amber-600 text-lg">&times;</button>
      </div>

      <div className="p-4 space-y-3">
        {/* Generate button */}
        {!results && (
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Thinking...
              </>
            ) : (
              <>Generate {mode === 'risks' ? 'Risk Suggestions' : mode === 'score' ? 'Score Suggestion' : 'Survey Questions'}</>
            )}
          </button>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        {/* Risk suggestions */}
        {mode === 'risks' && results?.risks && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {results.risks.map((risk, i) => (
              <div key={i} className="bg-white rounded-lg border border-amber-100 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h5 className="text-sm font-semibold text-slate-800 truncate">{risk.risk_title}</h5>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{risk.risk_description}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium">{risk.risk_category}</span>
                      {risk.regulatory_reference && (
                        <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px]">{risk.regulatory_reference}</span>
                      )}
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">
                        S:{risk.suggested_severity} L:{risk.suggested_likelihood}
                      </span>
                    </div>
                  </div>
                  {onAcceptRisk && (
                    <button
                      onClick={() => onAcceptRisk(risk)}
                      className="shrink-0 px-2.5 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors"
                    >
                      Accept
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button
              onClick={() => setResults(null)}
              className="w-full py-2 text-xs text-amber-600 hover:text-amber-800"
            >
              Regenerate
            </button>
          </div>
        )}

        {/* Score suggestion */}
        {mode === 'score' && results?.scores && (
          <div className="bg-white rounded-lg border border-amber-100 p-4 space-y-3">
            {Object.entries(results.scores).map(([key, val]) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-slate-700 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="text-xs text-slate-400 ml-2">({val.confidence} confidence)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-amber-700">{val.value}</span>
                </div>
              </div>
            ))}
            {results.overall_assessment && (
              <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">{results.overall_assessment}</p>
            )}
            <div className="flex gap-2 pt-2">
              {onAcceptScore && (
                <button
                  onClick={() => onAcceptScore(results.scores)}
                  className="flex-1 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium hover:bg-green-100"
                >
                  Apply Scores
                </button>
              )}
              <button onClick={() => setResults(null)} className="px-3 py-2 text-xs text-amber-600 border border-amber-200 rounded-lg hover:bg-amber-50">
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Survey questions */}
        {mode === 'survey' && results?.questions && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {results.questions.map((q, i) => (
              <div key={i} className="bg-white rounded-lg border border-amber-100 p-3">
                <div className="flex items-start gap-2">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800">{q.question_text}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px]">{q.question_type}</span>
                      {q.required && <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-[10px]">Required</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              {onAcceptQuestions && (
                <button
                  onClick={() => onAcceptQuestions(results.questions)}
                  className="flex-1 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium hover:bg-green-100"
                >
                  Add All to Survey
                </button>
              )}
              <button onClick={() => setResults(null)} className="px-3 py-2 text-xs text-amber-600 border border-amber-200 rounded-lg hover:bg-amber-50">
                Regenerate
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
